import { useCallback, useContext, useMemo } from 'react';

import { useScalingPolicies } from '@cast/features-cluster-shared';
import {
  ScalingPolicyAutocomplete,
  useAssignScalingPolicyDialog,
  useAutoAssignmentToggle,
  usePermitEditWorkload,
  WorkloadDetailsContext,
  canEditWorkload,
} from '@cast/split-me/features/cluster/workload-optimization';
import type { PreparedWorkload } from '@cast/split-me/features/workload-optimization';
import { WorkloadScalingPolicy } from '@cast/types';

type UseInventoryWorkloadScalingPolicyArgs = {
  workload: PreparedWorkload;
};

type UseInventoryWorkloadScalingPolicyReturn = {
  policies: WorkloadScalingPolicy[] | undefined;
  isLoading: boolean;
  isAutoAssignmentEnabled: boolean;
  isDisabled: boolean;
  isNamespaceWritableForWorkload: boolean;
  onChange: (policy: WorkloadScalingPolicy) => void;
  onHeaderClick: (() => void) | undefined;
};

export const useInventoryWorkloadScalingPolicy = ({
  workload,
}: UseInventoryWorkloadScalingPolicyArgs): UseInventoryWorkloadScalingPolicyReturn => {
  const { isNamespaceWritable } = useContext(WorkloadDetailsContext);
  const { permitEdit } = usePermitEditWorkload();
  const { policies, isLoading } = useScalingPolicies();
  const { openAssignPolicyModal } = useAssignScalingPolicyDialog();
  const {
    enableAutoAssignment,
    disableAutoAssignment,
    isAutoAssignmentEnabled,
  } = useAutoAssignmentToggle({ workload });

  const isNamespaceWritableForWorkload = useMemo(
    () => isNamespaceWritable(workload.namespace),
    [isNamespaceWritable, workload.namespace]
  );

  const canEdit = useMemo(
    () =>
      permitEdit && isNamespaceWritableForWorkload && canEditWorkload(workload),
    [workload, permitEdit, isNamespaceWritableForWorkload]
  );

  const handleChange = useCallback(
    (newSelectedPolicy: WorkloadScalingPolicy) => {
      if (isAutoAssignmentEnabled) {
        disableAutoAssignment(newSelectedPolicy);
      } else {
        openAssignPolicyModal({
          workload,
          newScalingPolicy: newSelectedPolicy,
        });
      }
    },
    [
      workload,
      isAutoAssignmentEnabled,
      disableAutoAssignment,
      openAssignPolicyModal,
    ]
  );

  const handleHeaderClick = useMemo(
    () => (canEdit ? enableAutoAssignment : undefined),
    [canEdit, enableAutoAssignment]
  );

  return {
    policies,
    isLoading,
    isAutoAssignmentEnabled,
    isDisabled: !canEdit,
    isNamespaceWritableForWorkload,
    onChange: handleChange,
    onHeaderClick: handleHeaderClick,
  };
};

export { ScalingPolicyAutocomplete };
